import { useState } from 'react';
import { Link, useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon, KpiCard } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { EmptyState } from '@ds/feedback';
import { dataSimples, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { descricaoDoGateway, pagamentoMenor, rotuloDaCobranca } from '@/data/gateways';
import { dados } from '@/data/porta';
import { pode } from '@/session/sessao';
import type { CargaDaClinicaNaPlataforma, DescontoDaClinica, Fatura, LinhaDeConsumoDeIa } from '@/data/tipos';
import { AlterarPlanoModal, DescontoModal, VagasDaClinicaModal } from './AcoesDaClinica';
import { CreditosDaClinica } from './CreditosDaClinica';
import { EstornoModal, FaturaAvulsaModal } from './ModaisDeFatura';
import { SITUACAO_DA_FATURA } from './apoioDoPainel';

const numero = (v: number) => v.toLocaleString('pt-BR');

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
    return (
        <>
            <dt style={{ color: 'var(--text-secondary)' }}>{rotulo}</dt>
            <dd style={{ margin: 0, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{children}</dd>
        </>
    );
}

/** A aba Assinatura da ficha: o plano, o preço (ou a falta dele), o uso sobre os limites e o desconto. */
export function AbaAssinatura({ carga }: { carga: CargaDaClinicaNaPlataforma }) {
    const [trocando, setTrocando] = useState(false);
    const [vagas, setVagas] = useState(false);
    const [desconto, setDesconto] = useState<'aplicar' | 'remover' | null>(null);
    const c = carga.clinica;
    const a = carga.assinatura;
    const plano = carga.planos.find((p) => p.codigo === c.plano);
    const vigente = carga.descontos.find((d) => d.vigente);

    /* As vagas vêm do banco (0165), não do plano: é a folga do cadastro que
       vence, e é esse número que o gatilho usa para recusar. Contar aqui pelo
       plano mostraria um limite que não é o que vale. */
    const limites = [
        { rotulo: 'Unidades', usado: carga.unidades.length, limite: plano?.limiteUnidades },
        {
            rotulo: 'Pessoas com acesso',
            usado: carga.vagas.ocupadas,
            limite: carga.vagas.limite ?? undefined,
            nota: carga.vagas.folgaDoCadastro === null
                ? undefined
                : `folga desta clínica${carga.vagas.limiteDoPlano === null ? '' : `, o plano dá ${carga.vagas.limiteDoPlano}`}`,
        },
    ];

    const colunasDeDesconto: DataTableColumn<DescontoDaClinica>[] = [
        { key: 'percentual', header: 'Desconto', width: 110, render: (d) => <strong style={{ fontWeight: 600 }}>{d.percentual}%</strong> },
        { key: 'inicioCompetencia', header: 'Competências', width: 170, render: (d) => `${d.inicioCompetencia} a ${d.fimCompetencia}` },
        { key: 'motivo', header: 'Motivo' },
        { key: 'autorNome', header: 'Por', width: 140, render: (d) => d.autorNome ?? '—' },
        {
            key: 'vigente', header: 'Situação', width: 130,
            render: (d) => (d.vigente
                ? <Badge tone="success" dot>Vigente</Badge>
                : <Badge tone="neutral">{d.revogadoEm ? 'Removido' : 'Encerrado'}</Badge>),
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(320px,100%),1fr))', gap: 16, alignItems: 'start' }}>
                <Card
                    title="Assinatura atual" padding={20}
                    actions={pode('plano.alterar') ? (
                        <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <Button size="sm" onClick={() => setTrocando(true)}>Alterar plano</Button>
                            <Button size="sm" variant="secondary" onClick={() => setVagas(true)}>Vagas de acesso</Button>
                            <Button size="sm" variant="secondary" onClick={() => setDesconto('aplicar')}>Aplicar desconto</Button>
                            {vigente && <Button size="sm" variant="ghost" onClick={() => setDesconto('remover')}>Remover desconto</Button>}
                        </span>
                    ) : undefined}
                >
                    {a ? (
                        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 20px', fontSize: 13 }}>
                            <Linha rotulo="Plano">{a.plano}</Linha>
                            <Linha rotulo="Valor">
                                {a.precoCentavos === undefined
                                    /* Preço não decidido não é zero: "a definir", escrito. */
                                    ? <span style={{ color: 'var(--warning-strong)' }}>a definir</span>
                                    : `${moeda(a.precoCentavos)} por ${a.ciclo === 'anual' ? 'ano' : 'mês'}`}
                            </Linha>
                            <Linha rotulo="Cliente desde">{dataSimples(a.inicio.slice(0, 10))}</Linha>
                            <Linha rotulo="Próxima cobrança">{a.canceladaEm ? 'cancelada' : dataSimples(a.proximaCobranca)}</Linha>
                            <Linha rotulo="Desconto">{vigente ? `${vigente.percentual}% até ${vigente.fimCompetencia}` : '—'}</Linha>
                            <Linha rotulo="Meio de pagamento">
                                <span style={{ color: 'var(--text-tertiary)' }}>nenhum — sem gateway, a baixa é manual</span>
                            </Linha>
                        </dl>
                    ) : (
                        <EmptyState compact icon="layers" title="Sem assinatura" description="Esta clínica não tem assinatura registrada. Trocar o plano cria uma." />
                    )}
                </Card>

                <Card title="Limites do plano" subtitle="O uso de hoje sobre o que o plano permite" padding={20}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {limites.map((l) => {
                            const acima = l.limite !== undefined && l.usado > l.limite;
                            return (
                                <div key={l.rotulo} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 13 }}>
                                    <span>{l.rotulo}</span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontVariantNumeric: 'tabular-nums' }}>
                                        <strong style={{ fontWeight: 600 }}>{l.usado}</strong>
                                        <span style={{ color: 'var(--text-tertiary)' }}>de {l.limite === undefined ? 'sem limite' : l.limite}</span>
                                        {l.nota && <span style={{ color: 'var(--text-tertiary)' }}>· {l.nota}</span>}
                                        {acima && <Badge tone="warning">acima do plano</Badge>}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            </div>

            <Card title="Descontos" subtitle="O vigente e o histórico — removido não é apagado" padding={0}>
                <DataTable
                    columns={colunasDeDesconto} rows={carga.descontos} rowKey="id"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhum desconto dado a esta clínica."
                />
            </Card>

            <AlterarPlanoModal clinica={c} planos={carga.planos} aberto={trocando} onFechar={() => setTrocando(false)} />
            <VagasDaClinicaModal clinica={c} vagas={carga.vagas} aberto={vagas} onFechar={() => setVagas(false)} />
            <DescontoModal clinica={c} vigente={vigente} modo={desconto} onFechar={() => setDesconto(null)} />
        </div>
    );
}

/** A aba Faturas da ficha: as faturas da clínica, a avulsa, o estorno e os créditos. */
export function AbaFaturasDaClinica({ carga }: { carga: CargaDaClinicaNaPlataforma }) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const [avulsa, setAvulsa] = useState(false);
    const [estornando, setEstornando] = useState<Fatura | null>(null);
    const c = carga.clinica;

    const baixar = async (f: Fatura) => {
        try {
            const hoje = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
            await dados().baixarFatura(f.id, f.pagoEm ? null : hoje);
            aviso({ title: f.pagoEm ? 'Baixa desfeita' : 'Pagamento registrado', description: `${f.competencia} · ${moeda(f.valorCentavos)}` });
            void revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível registrar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const [confirmar, confirmacao] = useConfirmacao();
    const cobranca = useCobrancaDasFaturas(confirmar);
    const colunas = colunasDeFatura({ onBaixar: baixar, onEstornar: setEstornando, comClinica: false, ...cobranca });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {confirmacao}
            <Card
                title="Faturas"
                subtitle={pode('financeiro.operar')
                    ? 'Assinatura e avulsas, da mais nova à mais antiga'
                    : 'Assinatura e avulsas, da mais nova à mais antiga · registrar pagamento pede a permissão “Abrir o faturamento e registrar pagamento”'}
                padding={0}
                actions={pode('faturamento.gerenciar') ? (
                    <Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setAvulsa(true)}>Fatura avulsa</Button>
                ) : undefined}
            >
                <DataTable
                    columns={colunas} rows={carga.faturas} rowKey="id"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhuma fatura desta clínica — sem preço no plano, não há mensalidade para gerar."
                />
            </Card>

            <CreditosDaClinica clinicaId={c.id} clinica={c.nome} creditos={carga.creditos} />

            <FaturaAvulsaModal aberto={avulsa} onFechar={() => setAvulsa(false)} clinicas={[{ id: c.id, nome: c.nome }]} />
            <EstornoModal fatura={estornando} onFechar={() => setEstornando(null)} />
        </div>
    );
}

/**
 * Criar, copiar e cancelar a cobrança de uma fatura no gateway (trilha T9).
 *
 * "Copiar link" é o reenvio: o painel não manda e-mail nem WhatsApp por conta
 * própria, e o operador cola o link no canal em que já fala com a clínica.
 */
export function useCobrancaDasFaturas(confirmar: ReturnType<typeof useConfirmacao>[0]) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();

    const cobrar = async (f: Fatura) => {
        try {
            const c = await dados().criarCobranca(f.id);
            aviso({ title: 'Cobrança criada', description: `${descricaoDoGateway(c.gateway).nome} · ${f.clinica} · ${moeda(f.valorCentavos)}`, tone: 'success' });
        } catch (e) {
            aviso({ title: 'A cobrança não foi criada', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            void revalidate();
        }
    };

    const copiarLink = async (f: Fatura) => {
        if (!f.cobrancaLink) return;
        try {
            await navigator.clipboard.writeText(f.cobrancaLink);
            aviso({ title: 'Link de pagamento copiado', description: 'envie à clínica pelo canal de sempre' });
        } catch {
            aviso({ title: 'Não foi possível copiar', description: f.cobrancaLink, tone: 'danger' });
        }
    };

    const cancelar = async (f: Fatura) => {
        const ok = await confirmar({
            titulo: `Cancelar a cobrança de ${f.clinica}?`,
            descricao: 'O link deixa de valer no gateway. A fatura continua em aberto: pode ser cobrada de novo ou baixada à mão.',
            confirmar: 'Cancelar cobrança',
        });
        if (!ok) return;
        try {
            await dados().cancelarCobranca(f.id);
            aviso({ title: 'Cobrança cancelada', description: `${f.clinica} · ${f.competencia}` });
        } catch (e) {
            aviso({ title: 'A cobrança não foi cancelada', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            void revalidate();
        }
    };

    return {
        onCobrar: (f: Fatura) => { void cobrar(f); },
        onCopiarLink: (f: Fatura) => { void copiarLink(f); },
        onCancelarCobranca: (f: Fatura) => { void cancelar(f); },
    };
}

/** O checkout da Stripe vence; link vencido não se copia, se refaz. */
function linkVencido(f: Fatura, agora = Date.now()): boolean {
    return Boolean(f.cobrancaExpiraEm) && new Date(f.cobrancaExpiraEm ?? '').getTime() <= agora;
}

/** As colunas de fatura, iguais na ficha e no faturamento. */
export function colunasDeFatura({ onBaixar, onEstornar, comClinica, onCobrar, onCopiarLink, onCancelarCobranca }: {
    onBaixar: (f: Fatura) => void;
    onEstornar: (f: Fatura) => void;
    comClinica: boolean;
    onCobrar?: (f: Fatura) => void;
    onCopiarLink?: (f: Fatura) => void;
    onCancelarCobranca?: (f: Fatura) => void;
}): DataTableColumn<Fatura>[] {
    return [
        ...(comClinica ? [{
            key: 'clinica', header: 'Clínica',
            render: (f: Fatura) => (
                <Link to={`/admin/clinicas/${f.clinicaId}`} style={{ color: 'var(--text-primary)', fontWeight: 600, textDecoration: 'none' }}>
                    {f.clinica}
                </Link>
            ),
        }] : []),
        {
            key: 'competencia', header: 'Competência', width: 180,
            render: (f) => (
                <span>
                    <span style={{ display: 'block' }}>{f.competencia}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {f.tipo === 'avulsa' ? `avulsa · ${f.descricao ?? ''}` : 'assinatura'}
                    </span>
                </span>
            ),
        },
        { key: 'vencimento', header: 'Vencimento', width: 130, render: (f) => dataSimples(f.vencimento) },
        {
            key: 'valorCentavos', header: 'Valor', align: 'right', width: 170,
            render: (f) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{moeda(f.valorCentavos)}</strong>
                    {(f.descontoCentavos ?? 0) > 0 && (
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>desconto {moeda(f.descontoCentavos ?? 0)}</span>
                    )}
                    {(f.creditoCentavos ?? 0) > 0 && (
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>crédito {moeda(f.creditoCentavos ?? 0)}</span>
                    )}
                </span>
            ),
        },
        {
            key: 'situacao', header: 'Situação', width: 210,
            render: (f) => {
                const cobranca = rotuloDaCobranca(f);
                const menor = pagamentoMenor(f);
                return (
                    <span>
                        <Badge tone={SITUACAO_DA_FATURA[f.situacao].tom} dot>{SITUACAO_DA_FATURA[f.situacao].rotulo}</Badge>
                        {cobranca && (
                            <span style={{ display: 'block', marginTop: 2, fontSize: 12, color: 'var(--text-tertiary)' }}>{cobranca}</span>
                        )}
                        {/* Nenhuma ação automática: o operador escolhe o que fazer com a diferença. */}
                        {menor && (
                            <span style={{ display: 'block', marginTop: 2, fontSize: 12, color: 'var(--warning-strong, var(--warning))' }}>
                                Pagamento menor que a fatura: recebido {moeda(menor.recebidoCentavos)}, faltam {moeda(menor.faltaCentavos)}
                            </span>
                        )}
                        {menor && (
                            <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>
                                cobre a diferença por fatura avulsa, estorne no gateway ou dê baixa manual
                            </span>
                        )}
                        {f.cobrancaErro && !f.pagoEm && (
                            <span style={{ display: 'block', marginTop: 2, fontSize: 12, color: 'var(--danger-strong, var(--danger))' }}>
                                a cobrança falhou: {f.cobrancaErro}
                            </span>
                        )}
                    </span>
                );
            },
        },
        {
            key: 'acoes', header: '', width: 380, align: 'right',
            render: (f) => (f.situacao === 'estornada' ? (
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }} title={f.estornoMotivo}>estornada em {dataSimples(f.estornadaEm)}</span>
            ) : (
                <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {/* Com pagamento menor, o link cobraria o valor inteiro de novo: sai. */}
                    {!f.pagoEm && !pagamentoMenor(f) && f.cobrancaLink && !linkVencido(f) && onCopiarLink && (
                        <Button size="sm" variant="ghost" onClick={() => onCopiarLink(f)}>Copiar link</Button>
                    )}
                    {!f.pagoEm && !pagamentoMenor(f) && f.valorCentavos > 0 && onCobrar && pode('faturamento.gerenciar') && (!f.cobrancaLink || linkVencido(f)) && (
                        <Button size="sm" variant="ghost" onClick={() => onCobrar(f)}>
                            {f.cobrancaErro || linkVencido(f) ? 'Cobrar de novo' : 'Cobrar'}
                        </Button>
                    )}
                    {!f.pagoEm && !pagamentoMenor(f) && f.cobrancaLink && onCancelarCobranca && pode('faturamento.gerenciar') && (
                        <Button size="sm" variant="ghost" onClick={() => onCancelarCobranca(f)}>Cancelar cobrança</Button>
                    )}
                    {pode('financeiro.operar') && (
                        <Button size="sm" variant="secondary" onClick={() => onBaixar(f)}>
                            {f.pagoEm ? 'Desfazer baixa' : 'Registrar pagamento'}
                        </Button>
                    )}
                    {f.situacao === 'paga' && pode('estorno.executar') && (
                        <Button size="sm" variant="ghost" onClick={() => onEstornar(f)}>Estornar</Button>
                    )}
                </span>
            )),
        },
    ];
}

/** A aba Consumo de IA da ficha: o que a clínica perguntou nos últimos 30 dias, e o teto dela. */
export function AbaIaDaClinica({ carga }: { carga: CargaDaClinicaNaPlataforma }) {
    const linhas = carga.consumoDeIa;
    const teto = carga.orcamentoDeIa;
    const perguntas = linhas.reduce((s, l) => s + l.perguntas, 0);
    const tokens = linhas.reduce((s, l) => s + l.tokensEntrada + l.tokensSaida, 0);
    const erros = linhas.reduce((s, l) => s + l.erros, 0);

    const colunas: DataTableColumn<LinhaDeConsumoDeIa & { id: string }>[] = [
        { key: 'provedor', header: 'Provedor', width: 140 },
        { key: 'modelo', header: 'Modelo' },
        { key: 'perguntas', header: 'Perguntas', align: 'right', width: 110, render: (l) => numero(l.perguntas) },
        { key: 'tokensEntrada', header: 'Tokens', align: 'right', width: 140, render: (l) => numero(l.tokensEntrada + l.tokensSaida) },
        { key: 'erros', header: 'Erros', align: 'right', width: 90, render: (l) => numero(l.erros) },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                <KpiCard label="Perguntas em 30 dias" value={numero(perguntas)} hint="aos agentes, fluxos e base" icon={<Icon name="sparkles" size={16} />} />
                <KpiCard label="Tokens em 30 dias" value={numero(tokens)} hint="entrada e saída somadas" icon={<Icon name="hash" size={16} />} />
                <KpiCard label="Erros" value={numero(erros)} hint="respostas que falharam" deltaTone={erros ? 'down' : 'neutral'} icon={<Icon name="triangle-alert" size={16} />} />
                <KpiCard
                    label="Teto do mês"
                    value={!teto || teto.limiteTokensMes === null ? 'sem teto' : numero(teto.limiteTokensMes)}
                    hint={teto ? `${numero(teto.tokensDoMes)} tokens usados neste mês${teto.estourado ? ' · IA parada' : ''}` : 'nenhum teto registrado'}
                    deltaTone={teto?.estourado ? 'down' : 'neutral'}
                    icon={<Icon name="gauge" size={16} />}
                />
            </div>

            <Card
                title="Consumo por modelo" subtitle="O texto das perguntas não é guardado: pergunta de clínica é dado da clínica" padding={0}
                actions={<Link to="/admin/ia" style={{ fontSize: 13, color: 'var(--text-link)', fontWeight: 600 }}>Definir o teto em Consumo de IA</Link>}
            >
                {linhas.length === 0 ? (
                    <EmptyState compact icon="sparkles" title="Nenhuma pergunta nos últimos 30 dias" description="Sem chave de IA na plataforma, ou a clínica não usou os agentes, os fluxos nem a base." />
                ) : (
                    <DataTable
                        columns={colunas} rows={linhas.map((l) => ({ ...l, id: `${l.provedor}-${l.modelo}` }))} rowKey="id"
                        stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    />
                )}
            </Card>
        </div>
    );
}
